import { isAnnotationBundleSet } from "@/api/trainingAnnotation";
import {
  useAnnotationStudioController,
  useAnnotationStudioSnapshot,
  type AnnotationFrameLocator,
  type AnnotationMediaResultBinding,
  type AnnotationSequenceSummary,
  type AnnotationStudioController,
  type AnnotationStudioControllerOptions,
  type AnnotationStudioControllerSnapshot,
} from "@carbon6/c6-frontend-design/annotation";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  commitAnnotationSet,
  createAnnotationSet,
  deleteAnnotationSet,
  getAnnotationSet,
  getAnnotationVideoUrl,
  listAnnotationSets,
  materializeAnnotationSet,
  renameAnnotationSet,
  type AnnotationSetSummaryView,
} from "@/api";
import { hatcheryCacheUid, hatcheryDatabase } from "@/app/hatcheryDatabase";
import { annotationUpstreamBlocked, gpuCapacityController } from "@/components/gpu";
import { AnnotationFrameBytesCache } from "./data/AnnotationFrameBytesCache";
import { AnnotationFrameMetaCache } from "./data/AnnotationFrameMetaCache";
import {
  useAnnotationSetDownload,
  type AnnotationSetDownloadController,
} from "./download/useAnnotationSetDownload";
import { RegistryAnnotationFrameMaterializer } from "./data/RegistryAnnotationFrameMaterializer";
import { RegistryAnnotationSavedFrameSource } from "./data/RegistryAnnotationSavedFrameSource";
import { RegistryAnnotationSequenceDirectorySource } from "./data/RegistryAnnotationSequenceDirectorySource";
import { RegistryAnnotationWorkspaceSource } from "./data/RegistryAnnotationWorkspaceSource";
import { RegistryAnnotationAuthoringDriver } from "./driver/RegistryAnnotationAuthoringDriver";
import { AnnotationFrameGeometryRegistry } from "./model/annotationCoordinates";
import { AnnotationIdentityRegistry } from "./model/annotationIdentity";
import {
  AnnotationTrackingController,
  annotationMediaSelection,
  trackingActionScope,
  trackingResultFor,
  type AnnotationTrackingActionScope,
  type AnnotationTrackingSnapshot,
} from "./tracking/AnnotationTrackingController";
import { TrackingConditioningCache } from "./tracking/trackingConditioning";

export type AnnotationSetListState =
  | Readonly<{ status: "idle" | "loading"; sets: readonly AnnotationSetSummaryView[] }>
  | Readonly<{ status: "ready"; sets: readonly AnnotationSetSummaryView[] }>
  | Readonly<{ status: "error"; sets: readonly AnnotationSetSummaryView[]; error: string }>;

export type AnnotationSetActionState = Readonly<{
  action: "create" | "rename" | "clone" | "commit" | "materialize" | "delete" | null;
  error: string | null;
}>;

export type AnnotationStudioBinding = {
  importContext: Readonly<{ datasetUuid: string; cameras: readonly string[] }>;
  controller: AnnotationStudioController;
  snapshot: AnnotationStudioControllerSnapshot;
  trackingController: AnnotationTrackingController;
  trackingSnapshot: AnnotationTrackingSnapshot;
  /**
   * 지금 화면에 표시할 트래킹 결과. 선택이 추적 대상과 다르면 null 이다. c6 는 결과가 붙어
   * 있는 동안 뷰포트와 타임라인을 결과 쪽에 고정한다. 그래서 다른 카메라에서 캡처를 가능하게
   * 하는 방법은 이 게이트뿐이다. 컨트롤러가 결과 자체를 계속 가지고 있으므로, 돌아오면 같은
   * 바인딩을 복원한다.
   */
  mediaResult: AnnotationMediaResultBinding | null;
  /** 트래킹 액션을 어디까지 그릴지. `elsewhere` 는 진행 중 실행의 제어만 남긴다. */
  trackingActionScope: AnnotationTrackingActionScope;
  sets: AnnotationSetListState;
  selectedSet: AnnotationSetSummaryView | null;
  selectedSetLoad: Readonly<{
    status: "idle" | "loading" | "ready" | "error";
    error: string | null;
  }>;
  setAction: AnnotationSetActionState;
  canCreateSet: boolean;
  refreshSets(): Promise<void>;
  selectSet(setUuid: string | null): Promise<boolean>;
  /** 빈 draft 를 만들고 그 세트로 세션을 연다. 이름은 만들 때 한 번 받는다. */
  createSet(name: string | null): Promise<boolean>;
  renameSelectedSet(name: string | null): Promise<boolean>;
  cloneSelectedSet(): Promise<boolean>;
  commitSelectedSet(): Promise<boolean>;
  retrySelectedSetMaterialize(): Promise<boolean>;
  deleteSelectedSet(): Promise<boolean>;
  /** 세트 파일 다운로드 상태다. 패널이 다시 마운트돼도 진행이 이어지도록 binding 이 소유한다. */
  download: AnnotationSetDownloadController;
};

export type UseAnnotationStudioOptions = {
  enabled?: boolean;
  collectionLabel?: string;
  totalSequenceCount?: number;
  cameras?: readonly string[];
};

type ContentIndex = {
  captured: readonly AnnotationSequenceSummary[];
  annotated: readonly AnnotationSequenceSummary[];
};

type SessionSelection = Readonly<{
  serial: number;
  initialSet: AnnotationSetSummaryView | null;
}>;

const STUDIO_LABELS = {
  collection: { en: "Dataset", ko: "Dataset" },
  sequence: { en: "Episode", ko: "에피소드" },
  stream: { en: "Camera", ko: "카메라" },
  target: { en: "Object", ko: "객체" },
} as const;

const INITIAL_ACTION: AnnotationSetActionState = { action: null, error: null };

export function useAnnotationStudio(
  datasetUuid: string,
  options: UseAnnotationStudioOptions = {},
): AnnotationStudioBinding {
  const {
    enabled = true,
    collectionLabel = datasetUuid,
    totalSequenceCount = 0,
    cameras = [],
  } = options;
  const contentRef = useRef<ContentIndex>({ captured: [], annotated: [] });
  const shared = useMemo(() => {
    const identities = new AnnotationIdentityRegistry(datasetUuid);
    const geometry = new AnnotationFrameGeometryRegistry();
    const workspace = new RegistryAnnotationWorkspaceSource(identities, collectionLabel, cameras);
    const directory = new RegistryAnnotationSequenceDirectorySource({
      datasetUuid,
      totalSequenceCount,
      identities,
      onCameras: (nextCameras) => workspace.publishCameras(nextCameras),
      getContentSequences: (scope, streamId) =>
        contentRef.current[scope].filter(
          (sequence) => streamId === null || sequence.streamIds.includes(streamId),
        ),
    });
    return {
      identities,
      geometry,
      workspace,
      directory,
      conditioning: new TrackingConditioningCache(),
      frameMeta: new AnnotationFrameMetaCache(),
      // 캐시가 키로 사용자를 구분한다. uid 는 데이터베이스의 수명을 정하는 곳에서 읽는다.
      frameBytes: new AnnotationFrameBytesCache({
        cache: hatcheryDatabase,
        getUid: hatcheryCacheUid,
      }),
    };
    // collection metadata is published below without replacing the session identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetUuid]);

  const [sets, setSets] = useState<AnnotationSetListState>({ status: "idle", sets: [] });
  const download = useAnnotationSetDownload();
  const [selectedSet, setSelectedSet] = useState<AnnotationSetSummaryView | null>(null);
  const [selection, setSelection] = useState<SessionSelection>({
    serial: 0,
    initialSet: null,
  });
  const [selectedSetLoad, setSelectedSetLoad] = useState<
    AnnotationStudioBinding["selectedSetLoad"]
  >({ status: "idle", error: null });
  const [setAction, setSetAction] = useState<AnnotationSetActionState>(INITIAL_ACTION);
  const actionPendingRef = useRef(false);

  const publishSetSummary = useCallback((next: AnnotationSetSummaryView) => {
    setSelectedSet(next);
    setSets((current) => ({
      status: current.status === "error" ? "ready" : current.status,
      sets: upsertSet(current.sets, next),
    }));
  }, []);

  const session = useMemo(
    () =>
      createAnnotationSession({
        datasetUuid,
        shared,
        initialSet:
          selection.initialSet?.dataset_uuid === datasetUuid ? selection.initialSet : null,
        onSetChanged: publishSetSummary,
      }),
    [datasetUuid, publishSetSummary, selection, shared],
  );

  const controllerOptions = useMemo(
    () =>
      ({
        workspaceSource: shared.workspace,
        sequenceDirectory: shared.directory,
        savedFrameIndex: session.savedFrames,
        frameMaterializer: session.frameMaterializer,
        mode: "authoring",
        authoring: session.authoring,
        locale: "ko",
        labels: STUDIO_LABELS,
        resolveMediaSource: shared.workspace.resolveMediaSource,
      }) satisfies AnnotationStudioControllerOptions,
    [session, shared],
  );
  const controller = useAnnotationStudioController(controllerOptions);
  const snapshot = useAnnotationStudioSnapshot(controller);
  const trackingSnapshot = useSyncExternalStore(
    session.tracking.subscribe,
    session.tracking.getSnapshot,
    session.tracking.getSnapshot,
  );
  // 표시 경계는 두 스냅샷에서 계산한다. 렌더링 중에 컨트롤러를 조회하면 구독한 스냅샷보다 나중
  // 상태를 읽을 수 있다. 그래서 선택자에 스냅샷을 그대로 넣는다.
  const mediaSelection = annotationMediaSelection(snapshot);
  const mediaResult = trackingResultFor(trackingSnapshot, mediaSelection);
  const actionScope = trackingActionScope(trackingSnapshot, mediaSelection);

  useEffect(() => {
    shared.workspace.publishCollectionLabel(collectionLabel);
    shared.workspace.publishCameras(cameras);
    shared.directory.publishTotalSequenceCount(totalSequenceCount);
  }, [cameras, collectionLabel, shared, totalSequenceCount]);

  useEffect(() => {
    if (enabled) shared.directory.start();
  }, [enabled, shared]);

  const refreshSets = useCallback(async (): Promise<void> => {
    setSets((current) => ({ status: "loading", sets: current.sets }));
    try {
      const next = await listAnnotationSets(datasetUuid);
      setSets({ status: "ready", sets: next });
      const current = session.set.current;
      const refreshed =
        current === null ? undefined : next.find((set) => set.uuid === current.uuid);
      if (
        current !== null &&
        refreshed !== undefined &&
        refreshed.revision === current.revision &&
        refreshed.status === current.status
      ) {
        session.applySet(refreshed);
      }
    } catch (error) {
      setSets((current) => ({
        status: "error",
        sets: current.sets,
        error: errorMessage(error),
      }));
    }
  }, [datasetUuid, session]);

  useEffect(() => {
    if (enabled && sets.status === "idle") void refreshSets();
  }, [enabled, refreshSets, sets.status]);

  useEffect(() => {
    if (!enabled) return;
    const set = session.set.current;
    if (set === null) {
      setSelectedSetLoad({ status: "ready", error: null });
      return;
    }
    const abort = new AbortController();
    setSelectedSetLoad({ status: "loading", error: null });
    void session.savedFrames
      .load(set, { signal: abort.signal })
      .then(() => {
        if (!abort.signal.aborted) setSelectedSetLoad({ status: "ready", error: null });
      })
      .catch((error: unknown) => {
        if (!abort.signal.aborted) {
          setSelectedSetLoad({ status: "error", error: errorMessage(error) });
        }
      });
    return () => abort.abort();
  }, [enabled, session]);

  useEffect(() => {
    if (!enabled || snapshot.selectedSequenceId === null || snapshot.selectedStreamId === null) {
      return;
    }
    const episode = shared.identities.episode(snapshot.selectedSequenceId);
    const camera = shared.identities.camera(snapshot.selectedStreamId);
    if (episode === null || camera === null) return;
    const abort = new AbortController();
    void getAnnotationVideoUrl(datasetUuid, camera, episode, { signal: abort.signal })
      .then((response) => {
        if (!abort.signal.aborted) {
          shared.workspace.publishMedia(snapshot.selectedSequenceId!, snapshot.selectedStreamId!, {
            src: response.url,
          });
        }
      })
      .catch(() => undefined);
    return () => abort.abort();
  }, [datasetUuid, enabled, shared, snapshot.selectedSequenceId, snapshot.selectedStreamId]);

  useEffect(() => {
    contentRef.current = {
      captured: sequenceContent(
        snapshot.draft?.orderedFrameIds
          .map((frameId) => snapshot.draft?.frames[frameId])
          .filter((frame): frame is NonNullable<typeof frame> => frame !== undefined) ?? [],
        shared.identities,
      ),
      annotated: sequenceContent(
        session.savedFrames.getSnapshot().orderedFrames,
        shared.identities,
      ),
    };
    shared.directory.refreshLocalFilters();
  }, [session, shared, snapshot.draft, snapshot.savedFrameIndex]);

  // StrictMode 와 Fast Refresh 는 같은 인스턴스에 mount → cleanup → mount 를 연속으로 실행한다.
  // c6 controller 는 dispose 뒤 다시 쓸 수 없으므로 여기서 dispose 하지 않는다. 구독·요청·리소스는
  // c6 hook 의 connect() cleanup 이 이미 해제한다. session 이 바뀌면 c6 hook 이 controller 를 새로 만든다.
  useEffect(() => () => session.tracking.dispose(), [session]);

  useEffect(
    () => () => {
      shared.directory.dispose();
      shared.conditioning.clear();
      shared.frameMeta.clear();
      shared.geometry.clear();
    },
    [shared],
  );

  const beginSession = useCallback((next: AnnotationSetSummaryView | null) => {
    setSelectedSet(next);
    setSelection((current) => ({ serial: current.serial + 1, initialSet: next }));
    setSelectedSetLoad({ status: next === null ? "ready" : "loading", error: null });
    setSetAction(INITIAL_ACTION);
  }, []);

  const selectSet = useCallback(
    async (setUuid: string | null): Promise<boolean> => {
      if (
        setUuid === session.set.current?.uuid ||
        (setUuid === null && session.set.current === null)
      ) {
        return true;
      }
      const allowed = await controller.requestLeave("collection-change");
      if (!allowed) return false;
      if (setUuid === null) {
        beginSession(null);
        return true;
      }
      try {
        const next =
          sets.sets.find((set) => set.uuid === setUuid) ?? (await getAnnotationSet(setUuid));
        if (next.dataset_uuid !== datasetUuid) throw new Error("annotation set dataset mismatch");
        setSets((current) => ({ status: "ready", sets: upsertSet(current.sets, next) }));
        beginSession(next);
        return true;
      } catch (error) {
        setSetAction({ action: null, error: errorMessage(error) });
        return false;
      }
    },
    [beginSession, controller, datasetUuid, session, sets.sets],
  );

  useEffect(() => {
    const materialize = selectedSet?.materialize;
    if (
      !enabled ||
      selectedSet === null ||
      (materialize?.status !== "queued" && materialize?.status !== "running")
    ) {
      return;
    }
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      if (cancelled) return;
      void getAnnotationSet(selectedSet.uuid)
        .then((next) => {
          if (!cancelled) session.applySet(next);
        })
        .catch(() => undefined);
    }, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [enabled, selectedSet, session]);

  const runAction = useCallback(
    async (
      action: Exclude<AnnotationSetActionState["action"], null>,
      operation: () => Promise<boolean | void>,
    ): Promise<boolean> => {
      if (actionPendingRef.current) return false;
      actionPendingRef.current = true;
      setSetAction({ action, error: null });
      try {
        const completed = await operation();
        setSetAction(INITIAL_ACTION);
        return completed !== false;
      } catch (error) {
        setSetAction({ action: null, error: errorMessage(error) });
        return false;
      } finally {
        actionPendingRef.current = false;
      }
    },
    [],
  );

  // 세트를 고르는 액션들은 선택이 있어야 성립한다. 선택이 없으면 요청을 보내지 않는다.
  const runSelectedAction = useCallback(
    (
      action: Exclude<AnnotationSetActionState["action"], null>,
      operation: (set: AnnotationSetSummaryView) => Promise<boolean | void>,
    ): Promise<boolean> => {
      const set = session.set.current;
      if (set === null) return Promise.resolve(false);
      return runAction(action, () => operation(set));
    },
    [runAction, session],
  );

  const canCreateSet =
    sets.status === "ready" &&
    selectedSetLoad.status === "ready" &&
    setAction.action === null &&
    !snapshot.isDirty &&
    !Object.values(snapshot.operations).some((operation) => operation.pending) &&
    trackingSnapshot.status !== "starting" &&
    trackingSnapshot.status !== "running";

  return {
    importContext: { datasetUuid, cameras },
    download,
    controller,
    snapshot,
    trackingController: session.tracking,
    trackingSnapshot,
    mediaResult,
    trackingActionScope: actionScope,
    sets,
    selectedSet,
    selectedSetLoad,
    setAction,
    canCreateSet,
    refreshSets,
    selectSet,
    createSet: (name) => {
      if (!canCreateSet) return Promise.resolve(false);
      return runAction("create", async () => {
        if (!(await controller.requestLeave("collection-change"))) return false;
        const created = await createAnnotationSet(datasetUuid, { name });
        setSets((current) => ({ status: "ready", sets: upsertSet(current.sets, created) }));
        beginSession(created);
        return true;
      });
    },
    renameSelectedSet: (name) =>
      runSelectedAction("rename", async (set) => {
        session.applySet(await renameAnnotationSet(set.uuid, { name }));
      }),
    cloneSelectedSet: () =>
      runSelectedAction("clone", async (set) => {
        if (isAnnotationBundleSet(set)) return false;
        if (set.status !== "committed") throw new Error("확정된 셋만 복제할 수 있습니다.");
        if (!(await controller.requestLeave("collection-change"))) return false;
        const clone = await createAnnotationSet(datasetUuid, {
          source_set_uuid: set.uuid,
          expected_source_revision: set.revision,
        });
        setSets((current) => ({ status: "ready", sets: upsertSet(current.sets, clone) }));
        beginSession(clone);
        return true;
      }),
    commitSelectedSet: () =>
      runSelectedAction("commit", async (set) => {
        if (isAnnotationBundleSet(set)) return false;
        if (!(await controller.requestLeave("collection-change"))) return false;
        // leave 확인에서 저장했다면 revision이 증가한다. 저장을 마친 같은 세트를 확정한다.
        const current = session.set.current;
        if (current?.uuid !== set.uuid) return false;
        const committed = await commitAnnotationSet(current.uuid, current.revision);
        setSets((current) => ({
          status: "ready",
          sets: upsertSet(current.sets, committed),
        }));
        beginSession(committed);
        return true;
      }),
    retrySelectedSetMaterialize: () =>
      runSelectedAction("materialize", async (set) => {
        if (isAnnotationBundleSet(set)) return false;
        session.applySet(await materializeAnnotationSet(set.uuid, set.revision));
        return true;
      }),
    deleteSelectedSet: () =>
      runSelectedAction("delete", async (set) => {
        if (!(await controller.requestLeave("collection-change"))) return false;
        await deleteAnnotationSet(set.uuid, set.revision);
        setSets((current) => ({
          status: "ready",
          sets: current.sets.filter((candidate) => candidate.uuid !== set.uuid),
        }));
        beginSession(null);
        return true;
      }),
  };
}

function createAnnotationSession(options: {
  datasetUuid: string;
  shared: {
    identities: AnnotationIdentityRegistry;
    geometry: AnnotationFrameGeometryRegistry;
    conditioning: TrackingConditioningCache;
    frameMeta: AnnotationFrameMetaCache;
    frameBytes: AnnotationFrameBytesCache;
  };
  initialSet: AnnotationSetSummaryView | null;
  onSetChanged: (set: AnnotationSetSummaryView) => void;
}) {
  const set = { current: options.initialSet };
  const savedFrames = new RegistryAnnotationSavedFrameSource(
    options.shared.identities,
    options.initialSet,
  );
  const applySet = (next: AnnotationSetSummaryView): void => {
    set.current = next;
    options.onSetChanged(next);
  };
  const frameMaterializer = new RegistryAnnotationFrameMaterializer({
    datasetUuid: options.datasetUuid,
    identities: options.shared.identities,
    geometry: options.shared.geometry,
    getSetUuid: () => set.current?.uuid ?? null,
    fetchFrame: options.shared.frameBytes.get,
  });
  // 캡처와 Tracking 상류는 GPU holder 위에서 실행되므로 둘 다 같은 판정을 쓴다. 스냅샷은 사이드바
  // 위젯이 폴링하는 값을 그대로 읽는다. 화면 갱신은 `AnnotationStudioTab` 이 구독으로 받는다.
  const getCapacityBlocked = (): boolean =>
    annotationUpstreamBlocked(gpuCapacityController.getSnapshot());
  const authoring = new RegistryAnnotationAuthoringDriver({
    datasetUuid: options.datasetUuid,
    identities: options.shared.identities,
    geometry: options.shared.geometry,
    savedFrames,
    getSelectedSet: () => set.current,
    onSetChanged: applySet,
    getCapacityBlocked,
    getFrameMeta: options.shared.frameMeta.get,
    fetchFrame: options.shared.frameBytes.get,
  });
  const tracking = new AnnotationTrackingController({
    datasetUuid: options.datasetUuid,
    identities: options.shared.identities,
    getSelectedSet: () => set.current,
    getCapacityBlocked,
    conditioning: options.shared.conditioning,
    getFrameMeta: options.shared.frameMeta.get,
  });
  return { set, savedFrames, frameMaterializer, authoring, tracking, applySet };
}

function upsertSet(
  sets: readonly AnnotationSetSummaryView[],
  next: AnnotationSetSummaryView,
): readonly AnnotationSetSummaryView[] {
  const existing = sets.findIndex((set) => set.uuid === next.uuid);
  if (existing < 0) return [next, ...sets];
  return sets.map((set, index) => (index === existing ? next : set));
}

function sequenceContent(
  frames: readonly AnnotationFrameLocator[],
  identities: AnnotationIdentityRegistry,
): readonly AnnotationSequenceSummary[] {
  const sequences = new Map<string, { label: string; streamIds: Set<string> }>();
  for (const frame of frames) {
    const episode = identities.episode(frame.sequenceId);
    if (episode === null) continue;
    const current = sequences.get(frame.sequenceId) ?? { label: episode, streamIds: new Set() };
    current.streamIds.add(frame.streamId);
    sequences.set(frame.sequenceId, current);
  }
  return [...sequences].map(([id, sequence]) => ({
    id,
    label: sequence.label,
    streamIds: [...sequence.streamIds],
  }));
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Annotation 요청을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.";
}
